import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { toFullMediaUrl } from '../../utils/mediaUrl';
import ProgressiveImg from './ProgressiveImg';
import './Avatar.css';



const GRADIENTS = [
  'linear-gradient(135deg, #006AB9 0%, #5BA8FF 100%)',
  'linear-gradient(135deg, #FF9900 0%, #FFB94D 100%)',
  'linear-gradient(135deg, #0A0E1A 0%, #1A2240 100%)',
  'linear-gradient(135deg, #006AB9 0%, #B04BFF 50%, #FF7A8A 100%)',
  'linear-gradient(135deg, #FF9900 0%, #E0384F 100%)',
  'linear-gradient(135deg, #0052B8 0%, #006AB9 100%)',
  'linear-gradient(135deg, #1F6FA5 0%, #5BA8FF 100%)',
  'linear-gradient(135deg, #E68A00 0%, #FF9900 100%)',
];

function hash(str = '') {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function initialsOf(name = '') {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '·';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const Avatar = ({ src, name = '', size = 'md', ring = false, className = '', alt, photoPosition, ...rest }) => {
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    setErrored(false);
  }, [src]);

  const gradient = useMemo(() => GRADIENTS[hash(name) % GRADIENTS.length], [name]);
  const initials = useMemo(() => initialsOf(name), [name]);

  const cachedSrc = useMemo(() => {
    if (!src) return src;
    // Resolve relative backend paths (e.g. "profile-photos/uuid.jpg")
    // against the backend's /files origin. Without this, the browser
    // would try to load the image from the frontend origin
    // (localhost:3000) and silently 404.
    const absolute = toFullMediaUrl(src);
    if (!absolute) return src;
    // Uploaded files use UUID paths. Do not add a changing query parameter:
    // it creates a second request for every avatar mount and defeats the
    // immutable cache configured for /files.
    return absolute;
  }, [src]);

  const showImg = cachedSrc && !errored;

  const cls = [
    'gm-avatar',
    `gm-avatar--${size}`,
    ring && 'gm-avatar--ring',
    className,
  ].filter(Boolean).join(' ');

  return (
    <span
      className={cls}
      style={showImg ? undefined : { background: gradient }}
      {...rest}
    >
      {showImg ? (
        <ProgressiveImg
          // A new UUID source gets a fresh ProgressiveImg state, while
          // repeated avatars share exactly the same cache key.
          key={cachedSrc}
          src={cachedSrc}
          alt={alt || name || 'avatar'}
          eager={size === 'xl' || size === '2xl'} /* big avatars = hero */
          imgClassName="gm-avatar__img"
          onClick={undefined}
          onError={() => setErrored(true)}
          fallback={<span aria-hidden="true">{initials}</span>}
          // Avatars are repeated throughout the UI. A 320px thumb is
          // more than enough for the largest avatar and avoids pulling
          // a multi-megapixel original into every comment/feed row.
          skipSrcSet
          sizes="48px"
          thumbWidth={320}
          imgStyle={photoPosition ? { objectPosition: photoPosition } : undefined}
        />
      ) : (
        <span>{initials}</span>
      )}
    </span>
  );
};

Avatar.propTypes = {
  src: PropTypes.string,
  name: PropTypes.string,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl']),
  ring: PropTypes.bool,
  className: PropTypes.string,
  alt: PropTypes.string,
  photoPosition: PropTypes.string,
};

export default Avatar;
